import { Link, NavLink, useNavigate } from "react-router-dom";
import { UtensilsCrossed, Menu, X, User, LogOut, LogIn, UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/calculator", label: "Calculator" },
  { to: "/laddoo", label: "Ladoo" },
  { to: "/poshan-panji", label: "Poshan Panji" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, isAuthed, logout } = useAuth();
  const nav = useNavigate();

  const doLogout = () => {
    logout();
    toast.info("Signed out");
    nav("/");
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-emerald-700 grid place-items-center text-white shadow-sm">
            <UtensilsCrossed size={20} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-slate-900 text-lg">PM Poshan</div>
            <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold -mt-0.5">Calculator · 2012–2100</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                  isActive ? "bg-emerald-50 text-emerald-800" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          {isAuthed ? (
            <>
              <Button variant="ghost" size="sm" asChild data-testid="header-dashboard-btn">
                <Link to="/dashboard">Dashboard</Link>
              </Button>
              <Button variant="ghost" size="sm" asChild data-testid="header-profile-btn">
                <Link to="/profile" className="flex items-center gap-1.5">
                  <User size={14}/> {user?.full_name?.split(" ")[0] || "Profile"}
                </Link>
              </Button>
              <Button variant="ghost" size="sm" onClick={doLogout} data-testid="header-logout-btn" className="text-red-700 hover:bg-red-50 hover:text-red-800">
                <LogOut size={14}/> Sign out
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild data-testid="header-premium-btn" className="text-orange-700 hover:bg-orange-50">
                <Link to="/premium">🔥 ₹299 Premium</Link>
              </Button>
              <Button variant="ghost" size="sm" asChild data-testid="header-login-btn">
                <Link to="/login" className="flex items-center gap-1.5"><LogIn size={14}/> Sign in</Link>
              </Button>
              <Button variant="outline" size="sm" asChild className="border-emerald-700 text-emerald-800 hover:bg-emerald-50" data-testid="header-register-btn">
                <Link to="/register" className="flex items-center gap-1.5"><UserPlus size={14}/> Register</Link>
              </Button>
            </>
          )}
          <Button variant="ghost" size="sm" asChild data-testid="header-admin-btn">
            <Link to="/admin">Admin</Link>
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="header-start-btn" asChild>
            <Link to="/calculator">Open Calculator</Link>
          </Button>
        </div>

        <button
          className="md:hidden p-2 rounded-md hover:bg-slate-100"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end
                onClick={() => setOpen(false)}
                data-testid={`mnav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2.5 rounded-md text-base font-semibold ${
                    isActive ? "bg-emerald-50 text-emerald-800" : "text-slate-700"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="border-t border-slate-100 my-2" />
            {isAuthed ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-2.5 text-base font-semibold text-slate-700 flex items-center gap-2" data-testid="mnav-dashboard">
                  <User size={16}/> Dashboard
                </Link>
                <Link to="/profile" onClick={() => setOpen(false)} className="px-3 py-2.5 text-base font-semibold text-slate-700 flex items-center gap-2" data-testid="mnav-profile">
                  <User size={16}/> Profile
                </Link>
                <button onClick={doLogout} className="px-3 py-2.5 text-left text-base font-semibold text-red-700 flex items-center gap-2" data-testid="mnav-logout">
                  <LogOut size={16}/> Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/premium" onClick={() => setOpen(false)} className="px-3 py-2.5 text-base font-semibold text-orange-700 flex items-center gap-2" data-testid="mnav-premium">
                  🔥 Premium ₹299 (Launch)
                </Link>
                <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2.5 text-base font-semibold text-slate-700 flex items-center gap-2" data-testid="mnav-login">
                  <LogIn size={16}/> Sign in
                </Link>
                <Link to="/register" onClick={() => setOpen(false)} className="px-3 py-2.5 text-base font-semibold text-emerald-800 flex items-center gap-2" data-testid="mnav-register">
                  <UserPlus size={16}/> Register free
                </Link>
              </>
            )}
            <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-2.5 text-base font-semibold text-slate-700" data-testid="mnav-admin">
              Admin
            </Link>
            <Link to="/calculator" onClick={() => setOpen(false)} className="mt-2 px-3 py-3 rounded-md bg-emerald-700 text-white text-center font-semibold" data-testid="mnav-start">
              Open Calculator
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
